import { requestKindOf } from '../requestForms.js';
import React from 'react';
const { useState, useEffect, useMemo } = React;

import { AirtableAPI, TABLE_IDS, CURRENT_USER, BASE_ID } from '../globals.js';
import ServiceStartPanel from './serviceStart/ServiceStartPanel.jsx';
import { F } from '../utils.jsx';
import { PIPELINE_STAGES, PIPELINE_FACT_WRITES, FACT_FIELDS, pipelineFactsOf, pipelineStageOf, factBadgesOf, confirmFactFields, scheduleMeetingFields, formatWhen, localToday } from '../pipelineFacts.js';
import { loadErrorOf } from '../dataLoad.js';
import { ErrorState } from './ui/States.jsx';
import { correctPaymentRecord, correctAcceptanceRecord } from '../quoteActions.js';
import { chainsOf, hasQuote, kindOf } from '../quoteChains.js';
import { contactNameOf, classifyRecipient } from '../personQuotes.js';
import { chargesCall } from '../chargesClient.js';
import { CHARGE } from '../billingSchema.js';

// The stages and the facts of each quote: src/pipelineFacts.js (docs/solicitudes/pipeline.md)
const STAGES = PIPELINE_STAGES;

function fmtMoney(n, currency) {
  if (!n || Number(n) === 0) return null;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(Number(n));
  } catch { return `${currency} ${Number(n).toLocaleString()}`; }
}

function totalFromLines(json, currency) {
  if (!json) return null;
  try {
    const lines = JSON.parse(json);
    const once    = lines.reduce((s, l) => s + (Number(l.onceFee)    || 0), 0);
    const monthly = lines.reduce((s, l) => s + (Number(l.monthlyFee) || 0), 0);
    return once || monthly ? { once, monthly, currency } : null;
  } catch { return null; }
}

export default function ConsultingPipeline({ data, api, onLogActivity, onUpdateRecord, canPrepare: canPrepareProp, canCorrect: canCorrectProp }) {
  const { accounts = [], diagnosticIntakes = [], landings = [], outreach = [], stakeholders = [] } = data;
  const [kindTab, setKindTab] = useState('company');
  // Versions with open charges: their payment is registered in Cotizaciones y pagos, never with the legacy fields
  const [chargedIds, setChargedIds] = useState(new Set());
  useEffect(() => {
    if (CURRENT_USER?.role !== 'admin') return;
    chargesCall('list').then(r => setChargedIds(new Set((r.charges || []).flatMap(c => c.fields?.[CHARGE.quote] || [])))).catch(() => {}); // cancelled ones too: the legacy status stays history
  }, []); // personal quotes are not company activity: their own filter
  const [intakes, setIntakes] = useState(diagnosticIntakes);
  const [loading, setLoading] = useState(false);
  const [ownLoadError, setOwnLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [cardError, setCardError] = useState({}); // record id → message of a failed save
  const [notices, setNotices] = useState({}); // record id → the last correction (kept when the card changes column)
  const [preparing, setPreparing] = useState(null); // the accepted quote whose service start is being prepared
  // Correcting a payment registered by mistake: an administrator of the base (in the central base, Oike's); the server checks it again
  const canCorrect = canCorrectProp ?? CURRENT_USER?.role === 'admin';
  // Preparing a service reaches Diagnostic and Acompañamiento: Oike administration only (the servers check it again)
  const canPrepare = canPrepareProp ?? (CURRENT_USER?.role === 'admin' && (BASE_ID === 'appilTHSsdc8Ioyvg' || CURRENT_USER?.environment === 'test'));
  // The quotes could not be read: never shown as an empty pipeline
  const loadError = loadErrorOf(data, 'diagnosticIntakes') || ownLoadError;

  // A background refresh brings new rows (a failed one keeps the previous ones: src/dataLoad.js)
  useEffect(() => { if (data.diagnosticIntakes?.length) setIntakes(data.diagnosticIntakes); }, [data.diagnosticIntakes]);

  const reload = () => {
    setLoading(true); setOwnLoadError('');
    const a = api || new AirtableAPI();
    a.fetchTable(TABLE_IDS.diagnosticIntake)
      .then(rows => setIntakes(rows))
      .catch(e => { console.error('pipeline load:', e); setOwnLoadError(e.message || 'No se pudo conectar'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (diagnosticIntakes.length > 0 || loadErrorOf(data, 'diagnosticIntakes')) return;
    reload();
  }, []);

  // A webinar sign-up is not a sales opportunity (src/requestForms.js)
  // One card per quote (src/quoteChains.js): the operative version of each chain — its latest accepted
  // version while a revision is in preparation, otherwise the current one — never every revision.
  // Form requests without a quote stay as before.
  const enriched = useMemo(() => {
    const live = intakes.filter(r => requestKindOf(r.fields || {}).type !== 'webinar');
    const chainInfo = new Map();
    for (const c of chainsOf(live)) {
      // The operative version (src/quoteChains.js): what the client has; a draft revision never hides it
      const operative = c.operative || c.current || c.versions[c.versions.length - 1];
      chainInfo.set(operative.id, { versions: c.versions.length, number: operative.number, pendingNumber: c.pendingRevision?.number || null, replacedCount: c.replaced.length, anomalies: c.anomalies });
    }
    return live.filter(r => !hasQuote(r.fields || {}) || chainInfo.has(r.id)).map(r => {
      const f = r.fields || {};
      const accId = Array.isArray(f['Account']) ? f['Account'][0] : null;
      // Form requests without a quote stay where they were; a quote goes by its recipient, never guessed
      const kind = hasQuote(f) ? kindOf(f) : 'company';
      const personal = kind === 'personal';
      // A personal quote is never shown under a company, even with one linked as a reference
      const account = kind === 'company' && accId ? accounts.find(a => a.id === accId) : null;
      const person = personal ? contactNameOf(stakeholders.find(st => st.id === (f['Stakeholder'] || [])[0])) : '';
      const landingId = Array.isArray(f['Consulting Landing']) ? f['Consulting Landing'][0] : null;
      const landing = landingId ? landings.find(l => l.id === landingId) : null;
      const facts = pipelineFactsOf(f, { landing, outreach });
      const stage = pipelineStageOf(facts);
      const totals = totalFromLines(f['Quote Lines JSON'], f['Quote Currency'] || 'USD');
      return { ...r, _account: account, _person: person, _personal: personal, _kind: kind, _reference: personal && accId ? accounts.find(a => a.id === accId) : null, _chain: chainInfo.get(r.id) || null, _stage: stage, _facts: facts, _totals: totals };
    });
  }, [intakes, accounts, landings, outreach, stakeholders]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return enriched.filter(r => {
      if (r._kind !== kindTab) return false;
      if (!q) return true;
      const name = r._person || F(r._account, 'Account Name') || F(r, 'Name') || '';
      return name.toLowerCase().includes(q);
    });
  }, [enriched, search, kindTab]);

  const byStage = useMemo(() => {
    const map = {};
    for (const s of STAGES) map[s.key] = [];
    for (const r of filtered) (map[r._stage] || map['intake-sent']).push(r);
    return map;
  }, [filtered]);

  const applyLocal = (id, fields) => {
    setIntakes(prev => prev.map(r => r.id === id ? { ...r, fields: { ...r.fields, ...fields } } : r));
    if (onUpdateRecord) onUpdateRecord('diagnosticIntakes', id, fields);
  };

  // An acceptance is registered in the quote's file (the same for every access): it freezes the accepted document
  const openFile = (record) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'quotes', quoteId: record.id } }));

  // Registers one fact (already happened) or schedules a meeting (a plan, may be future),
  // with the day Ale gives and an optional time. Pipeline Facts Note keeps its other lines.
  const handleSaveWhen = async (record, { key, at, time, via }) => {
    setCardError(prev => ({ ...prev, [record.id]: '' }));
    try {
      const note = record.fields?.[FACT_FIELDS.factsNote] || '';
      const fields = key === 'scheduleMeeting'
        ? scheduleMeetingFields({ at, time, note })
        : confirmFactFields(key, { at, time, via, note });
      const a = api || new AirtableAPI();
      await a.updateRecord(TABLE_IDS.diagnosticIntake, record.id, fields);
      applyLocal(record.id, fields);
      return true;
    } catch (e) {
      setCardError(prev => ({ ...prev, [record.id]: 'No se pudo guardar: ' + e.message }));
      return false;
    }
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--globant-muted)' }}>Cargando el pipeline…</div>
  );

  const totalCount = filtered.length;
  const wonCount   = filtered.filter(r => r._stage === 'won').length;

  return (
    <div>
      <div className="page-header">
        <h1>Pipeline</h1>
        <p>Del primer contacto al cliente ganado. Cada hecho se confirma por separado: aprobar no es reunirse, publicar no es enviar y ganar no es cobrar.</p>
      </div>

      {loadError && (
        <div style={{ marginBottom: 16 }}>
          <ErrorState message={`No se pudo cargar el pipeline (${loadError}).${intakes.length ? ' Se muestra la última carga.' : ''}`} onRetry={reload} />
        </div>
      )}

      {/* ── Summary bar ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {STAGES.map(s => {
          const n = byStage[s.key].length;
          if (!n) return null;
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px',
              background: 'rgba(30,42,44,0.04)', border: '1px solid var(--globant-border)', borderRadius: 20 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.color, flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontSize: 11, color: 'var(--globant-muted)', fontWeight: 600 }}>{s.label}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-text)' }}>{n}</span>
            </div>
          );
        })}
      </div>

      {/* Nothing could be read: no empty columns that would look like "no opportunities" */}
      {!(loadError && intakes.length === 0) && <>
      {/* ── Company or personal quotes: never mixed ── */}
      <div role="tablist" style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['company', 'Empresas'], ['personal', 'Personas'], ['review', 'Por revisar']].filter(([k]) => k !== 'review' || enriched.some(r => r._kind === 'review')).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={kindTab === k} type="button" className={`action-btn ${kindTab === k ? 'btn-primary' : 'btn-ghost'}`} style={{ fontSize: 12 }} onClick={() => setKindTab(k)}>
            {l} ({enriched.filter(r => r._kind === k).length})
          </button>
        ))}
      </div>
      {/* ── Search ── */}
      <div style={{ marginBottom: 20 }}>
        <input className="input-field" style={{ fontSize: 12, width: '100%', maxWidth: 340 }}
          placeholder="Buscar empresa…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* ── Kanban ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 14, alignItems: 'start' }}>
        {STAGES.map(({ key, label, color }) => (
          <div key={key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block' }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--globant-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</span>
              <span style={{ fontSize: 10, background: 'rgba(30,42,44,0.06)', color: 'var(--globant-muted)', borderRadius: 10, padding: '1px 7px', fontWeight: 600 }}>
                {byStage[key].length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byStage[key].length === 0 && (
                <div style={{ fontSize: 11, color: 'var(--globant-muted)', padding: '12px', background: 'rgba(30,42,44,0.02)', borderRadius: 8, border: '1px dashed var(--globant-border)', textAlign: 'center' }}>
                  —
                </div>
              )}
              {byStage[key].map(r => (
                <PipelineCard key={r.id} record={r} color={color} stageKey={key}
                  onOpenFile={() => openFile(r)}
                  onSaveWhen={(w) => handleSaveWhen(r, w)} error={cardError[r.id]}
                  onPrepare={canPrepare && key === 'won' ? () => setPreparing(r) : null}
                  hasCharges={chargedIds.has(r.id)}
                  onCorrected={canCorrect ? (fields, message) => { applyLocal(r.id, fields); setNotices(n => ({ ...n, [r.id]: message || '' })); } : null}
                  notice={notices[r.id]} />
              ))}
            </div>
          </div>
        ))}
      </div>

      </>}

      {preparing && (
        <ServiceStartPanel quote={preparing} accountName={F(preparing._account, 'Account Name') || ''} facts={preparing._facts} onClose={() => setPreparing(null)} />
      )}

      {totalCount === 0 && !loadError && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--globant-muted)', fontSize: 13 }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🎯</div>
          <div style={{ fontWeight: 600, color: 'var(--globant-text)', marginBottom: 6 }}>Todavía no hay oportunidades</div>
          <div>Crea un link de cotización desde la pestaña Cotización de una empresa.</div>
        </div>
      )}
    </div>
  );
}

const BADGE_TONE = {
  confirmed: { bg: 'rgba(30,123,79,0.10)', color: '#1E7B4F' },
  pending: { bg: 'rgba(154,98,18,0.10)', color: '#9A6212' },
  partial: { bg: 'rgba(154,98,18,0.10)', color: '#9A6212' },
  cancelled: { bg: 'rgba(30,42,44,0.06)', color: 'var(--globant-muted)' },
};
// Today in the viewer's own calendar (not UTC: after 21:00 in Argentina UTC is already tomorrow)
const todayInput = () => localToday();
// The calendar day of a date: a chosen day as is; a moment in the viewer's time zone
const dayOfWhen = (at, precision) => (precision === 'day' ? at.slice(0, 10) : localToday(new Date(at)));
const WHEN_LABEL = { won: 'Día en que aceptó', paymentReceived: 'Día en que se completó el pago', scheduleMeeting: 'Día de la reunión', meetingHeld: 'Día en que se realizó', proposalSent: 'Día en que se envió', serviceStarted: 'Día en que empezó' };

function PipelineCard({ record, color, stageKey, onOpenFile, onSaveWhen, error, onPrepare, onCorrected, notice, hasCharges = false }) {
  const f = record.fields || {};
  const accountName = record._person || F(record._account, 'Account Name') || F(record, 'Name') || '—';
  const industry    = F(record._account, 'Industry') || '';
  const submittedAt = f['Submitted At'];
  const totals      = record._totals;
  // The column's own fact is shown only when it adds a date
  const OWN = { won: 'won', 'proposal-published': 'proposalPublished', 'proposal-sent': 'proposalSent', 'meeting-held': 'meetingHeld' };
  // With open charges, the payment is not a pipeline fact any more: it lives in its charges
  const badges      = factBadgesOf(record._facts).filter(b => !(b.key === OWN[stageKey] && !b.at)).filter(b => !(hasCharges && b.key === 'paymentReceived'));
  const [confirming, setConfirming] = useState(null); // { key, at, time, via }; key 'scheduleMeeting' is a plan
  const meeting = record._facts.meetingHeld;
  const canSchedule = PIPELINE_FACT_WRITES && ['quote-draft', 'quote-approved', 'proposal-published', 'proposal-sent'].includes(stageKey)
    && meeting.state !== 'confirmed' && meeting.state !== 'cancelled' && meeting.source !== 'status-without-date';
  const open = (key, extra = {}) => setConfirming({ key, at: todayInput(), time: '', via: '', ...extra });
  const isPlan = confirming?.key === 'scheduleMeeting';
  const [savingFact, setSavingFact] = useState(false);
  const canWin = stageKey === 'proposal-published' || stageKey === 'proposal-sent';

  const saveFact = async () => {
    setSavingFact(true);
    const ok = await onSaveWhen(confirming);
    setSavingFact(false);
    if (ok) setConfirming(null); // on failure the form stays with what was typed
  };

  return (
    <div className="card" style={{ padding: '13px 14px', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--globant-text)', marginBottom: 2 }}>{accountName}</div>
      {industry && <div style={{ fontSize: 11, color: 'var(--globant-muted)', marginBottom: 8 }}>{industry}</div>}
      {hasCharges && <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginBottom: 6 }}>Pago: en sus cobros (Cotizaciones y pagos)</div>}
      {record._personal && <div style={{ fontSize: 11, color: 'var(--globant-muted)', marginBottom: 6 }}>Cotización personal{F(record._reference, 'Account Name') ? ` · trabaja en ${F(record._reference, 'Account Name')} (referencia)` : ''}</div>}
      {record._kind === 'review' && <div style={{ fontSize: 11, color: '#9A6212', marginBottom: 6 }}>Destinatario por revisar: {classifyRecipient(record.fields || {}).reasons.join(' · ')}</div>}
      {record._chain?.versions > 1 && (
        <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginBottom: 6 }}>
          v{record._chain.number} de {record._chain.versions} (operativa){record._chain.replacedCount ? ` · reemplaza a ${record._chain.replacedCount === 1 ? 'una versión anterior' : `${record._chain.replacedCount} versiones anteriores`}` : ''} · historial en Cotizaciones y pagos
        </div>
      )}
      {record._chain?.pendingNumber && <div role="note" style={{ fontSize: 10, fontWeight: 600, color: '#9A6212', marginBottom: 6 }}>Revisión en preparación (v{record._chain.pendingNumber}): el cliente tiene esta versión.</div>}
      {record._chain?.anomalies?.length > 0 && <div role="note" style={{ fontSize: 10, color: '#9A6212', marginBottom: 6 }}>Versiones para revisar (Cotizaciones y pagos)</div>}

      {totals && (
        <div style={{ fontSize: 11, color: '#9A6212', fontWeight: 600, marginBottom: 6 }}>
          {[
            totals.once    > 0 ? fmtMoney(totals.once,    totals.currency) : null,
            totals.monthly > 0 ? `${fmtMoney(totals.monthly, totals.currency)}/mes` : null,
          ].filter(Boolean).join(' · ')}
        </div>
      )}

      {submittedAt && (
        <div style={{ fontSize: 10, color: 'var(--globant-muted)', marginBottom: 6 }}>
          Formulario {new Date(submittedAt).toLocaleDateString('es', { day: 'numeric', month: 'short' })}
        </div>
      )}

      {/* The facts, each with its own state: confirmed (with its date, if there is one) or pending */}
      {badges.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          {badges.map(b => (
            <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span title={b.why || undefined} style={{ fontSize: 10, fontWeight: 600, borderRadius: 10, padding: '2px 8px', background: BADGE_TONE[b.state]?.bg, color: BADGE_TONE[b.state]?.color }}>
                {b.state === 'confirmed' ? '✓ ' : ''}{b.label}{b.at ? ` · ${formatWhen(b.at, b.precision)}` : ''}
                {b.plannedAt ? ` · para el ${formatWhen(b.plannedAt, b.plannedPrecision)}${dayOfWhen(b.plannedAt, b.plannedPrecision) < todayInput() ? ' · ¿se realizó?' : ''}` : ''}
              </span>
              {(b.state === 'pending' || (b.state === 'partial' && b.key === 'paymentReceived')) && PIPELINE_FACT_WRITES && !confirming
                && !(b.plannedAt && dayOfWhen(b.plannedAt, b.plannedPrecision) > todayInput()) && (
                <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10, padding: '1px 8px' }}
                  onClick={() => (b.key === 'won' ? onOpenFile() : open(b.key, b.plannedAt && dayOfWhen(b.plannedAt, b.plannedPrecision) <= todayInput() ? { at: dayOfWhen(b.plannedAt, b.plannedPrecision) } : {}))}>{b.key === 'won' ? 'Registrar en la ficha' : b.plannedAt ? 'Registrar que se realizó' : 'Registrar'}</button>
              )}
            </div>
          ))}
          {!PIPELINE_FACT_WRITES && badges.some(b => b.state === 'pending') && (
            <div style={{ fontSize: 10, color: 'var(--globant-muted)', lineHeight: 1.4 }}>Los pendientes se podrán registrar cuando estén los campos nuevos en Airtable.</div>
          )}
        </div>
      )}

      {confirming && (
        <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 6 }}>
            <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>{WHEN_LABEL[confirming.key] || 'Día en que ocurrió'}
              {/* A fact already happened: never a future day. A meeting to schedule may be in the future. */}
              <input type="date" className="input-field" style={{ fontSize: 11, width: '100%' }} value={confirming.at} max={isPlan ? undefined : todayInput()}
                onChange={e => setConfirming(c => ({ ...c, at: e.target.value }))} />
            </label>
            <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Hora (opcional)
              <input type="time" className="input-field" style={{ fontSize: 11, width: '100%' }} value={confirming.time}
                onChange={e => setConfirming(c => ({ ...c, time: e.target.value }))} />
            </label>
          </div>
          <div style={{ fontSize: 10, color: 'var(--globant-muted)', lineHeight: 1.4 }}>{confirming.time ? 'Se guarda el momento exacto, en tu zona horaria.' : 'Sin hora se guarda solo el día, tal como lo elegís.'}</div>
          {confirming.key === 'proposalSent' && (
            <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Por dónde (opcional)
              <input className="input-field" style={{ fontSize: 11, width: '100%' }} value={confirming.via} maxLength={80} placeholder="Email, WhatsApp, en la reunión…"
                onChange={e => setConfirming(c => ({ ...c, via: e.target.value }))} />
            </label>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" className="action-btn btn-primary" style={{ fontSize: 10, flex: 1 }} disabled={savingFact || !confirming.at} onClick={saveFact}>{savingFact ? 'Guardando…' : 'Guardar'}</button>
            <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10 }} disabled={savingFact} onClick={() => setConfirming(null)}>Cancelar</button>
          </div>
        </div>
      )}

      {canWin && !confirming && (
        <button className="action-btn btn-ghost" style={{ fontSize: 10, marginTop: 8, color: '#1E7B4F', borderColor: 'rgba(52,211,153,0.3)', width: '100%' }}
          onClick={onOpenFile}>
          Registrar aceptación (en la ficha)
        </button>
      )}

      {canSchedule && !confirming && (
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10, marginTop: 6, width: '100%' }}
          onClick={() => open('scheduleMeeting', meeting.plannedAt ? { at: dayOfWhen(meeting.plannedAt, meeting.plannedPrecision) } : {})}>
          {meeting.plannedAt ? 'Cambiar fecha de la reunión' : 'Agendar reunión'}
        </button>
      )}

      {onPrepare && !confirming && (
        <button type="button" className="action-btn btn-primary" style={{ fontSize: 10, marginTop: 8, width: '100%' }} onClick={onPrepare}>
          Preparar inicio del servicio
        </button>
      )}

      {onCorrected && !confirming && !hasCharges && (f[FACT_FIELDS.paymentStatus] || f[FACT_FIELDS.paymentReceivedAt]) && (
        <PaymentCorrection record={record} onCorrected={onCorrected} onStart={() => onCorrected({}, '')} />
      )}
      {onCorrected && !confirming && stageKey === 'won' && (
        <AcceptanceCorrection record={record} onCorrected={onCorrected} onStart={() => onCorrected({}, '')} />
      )}
      {notice && <div role="status" style={{ fontSize: 10, color: '#1E7B4F', marginTop: 4, lineHeight: 1.4 }}>{notice}</div>}

      {error && <div role="alert" style={{ fontSize: 11, color: '#B4362C', marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// Correcting an acceptance (netlify/functions/quote-correction.mts): a mistaken registration and a real
// cancellation by the client are different facts and stay apart. Neither unlocks the lines, changes the
// emitted document, or touches payments and linked services; nothing is cancelled or refunded here.
function AcceptanceCorrection({ record, onCorrected, onStart }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const start = () => { setForm({ type: '', day: '', reason: '', confirm: false }); setErr(''); onStart(); };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const r = await correctAcceptanceRecord(record.id, { type: form.type, cancelledOn: form.day, reason: form.reason });
      setForm(null);
      onCorrected(r.fields || {}, `Aceptación corregida: antes «${r.before}», ahora «${r.after}». Quedó anotado en la nota de hechos.`);
    } catch (e) { setErr(e.message); } // the form keeps what was typed
    setSaving(false);
  };
  if (!form) return (
    <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10, width: '100%', marginTop: 6 }} onClick={start}>Corregir aceptación</button>
  );
  const reasonOk = form.reason.trim().length >= 10 && form.reason.trim().length <= 500;
  return (
    <div style={{ marginTop: 8, display: 'grid', gap: 6, padding: 8, border: '1px dashed var(--globant-border)', borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--globant-muted)', lineHeight: 1.4 }}>
        Las líneas y el documento emitido no cambian (un cambio de contenido es una revisión nueva). Pagos y servicios vinculados quedan como están: no se cancela, no se retiran accesos ni se devuelve dinero.
      </div>
      <label style={{ fontSize: 10, color: 'var(--globant-text)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <input type="radio" name={`acc-${record.id}`} checked={form.type === 'error'} onChange={() => setForm(c => ({ ...c, type: 'error', day: '' }))} />
        Se registró por error: el cliente no la aceptó. Vuelve a «aprobada» y deja de contar como ganada.
      </label>
      <label style={{ fontSize: 10, color: 'var(--globant-text)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <input type="radio" name={`acc-${record.id}`} checked={form.type === 'cancelled'} onChange={() => setForm(c => ({ ...c, type: 'cancelled' }))} />
        El cliente la aceptó y después canceló. Queda como «cancelada por el cliente», con su fecha de aceptación.
      </label>
      {form.type === 'cancelled' && (
        <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Día en que canceló (opcional)
          <input type="date" className="input-field" style={{ fontSize: 11, width: '100%' }} value={form.day} max={todayInput()} onChange={e => setForm(c => ({ ...c, day: e.target.value }))} />
        </label>
      )}
      <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Motivo (obligatorio)
        <textarea className="input-field" rows={2} maxLength={500} style={{ fontSize: 11, width: '100%' }} value={form.reason} onChange={e => setForm(c => ({ ...c, reason: e.target.value }))} />
      </label>
      <label style={{ fontSize: 10, color: 'var(--globant-text)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={form.confirm} onChange={e => setForm(c => ({ ...c, confirm: e.target.checked }))} />
        Confirmo esta corrección.
      </label>
      {err && <div role="alert" style={{ fontSize: 10, color: '#B4362C' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="action-btn btn-primary" style={{ fontSize: 10, flex: 1 }} disabled={saving || !form.type || !form.confirm || !reasonOk} onClick={save}>{saving ? 'Guardando…' : 'Guardar corrección'}</button>
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10 }} disabled={saving} onClick={() => setForm(null)}>Cancelar</button>
      </div>
    </div>
  );
}

const PAYMENT_LABEL = { '': 'Sin pago registrado', Pending: 'Pendiente', Partial: 'Parcial', Received: 'Completado' };

// The explicit, traceable way to fix a payment registered by mistake (netlify/functions/quote-correction.mts):
// a new state (or none), the day when completed, a written reason and a confirmation. The server
// writes only the payment and prepends one line to the facts note (before, after, who, when, why).
function PaymentCorrection({ record, onCorrected, onStart }) {
  const f = record.fields || {};
  const currentStatus = (f[FACT_FIELDS.paymentStatus]?.name || f[FACT_FIELDS.paymentStatus] || '');
  const currentDay = String(f[FACT_FIELDS.paymentReceivedAt] || '').slice(0, 10);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const start = () => { setForm({ status: currentStatus, day: currentDay || todayInput(), reason: '', confirm: false }); setErr(''); onStart(); };
  const save = async () => {
    setSaving(true); setErr('');
    try {
      const r = await correctPaymentRecord(record.id, { status: form.status, receivedOn: form.day, reason: form.reason });
      setForm(null);
      onCorrected(r.fields || {}, `Pago corregido: antes «${r.before}», ahora «${r.after}». Quedó anotado en la nota de hechos.`);
    } catch (e) { setErr(e.message); } // the form keeps what was typed
    setSaving(false);
  };
  if (!form) return (
    <div style={{ marginTop: 6 }}>
      <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10, width: '100%' }} onClick={start}>Corregir pago registrado</button>
    </div>
  );
  const reasonOk = form.reason.trim().length >= 10 && form.reason.trim().length <= 500;
  return (
    <div style={{ marginTop: 8, display: 'grid', gap: 6, padding: 8, border: '1px dashed var(--globant-border)', borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--globant-muted)', lineHeight: 1.4 }}>
        Para corregir un error de registro. No es una devolución ni una anulación: cambia solo el estado y el día del pago, y deja anotado qué había, qué queda, quién y por qué.
      </div>
      <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Estado correcto
        <select className="input-field" style={{ fontSize: 11, width: '100%' }} value={form.status} onChange={e => setForm(c => ({ ...c, status: e.target.value }))}>
          {Object.entries(PAYMENT_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      {form.status === 'Received' && (
        <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Día en que se completó
          <input type="date" className="input-field" style={{ fontSize: 11, width: '100%' }} value={form.day} max={todayInput()} onChange={e => setForm(c => ({ ...c, day: e.target.value }))} />
        </label>
      )}
      <label style={{ fontSize: 10, color: 'var(--globant-muted)' }}>Motivo (obligatorio)
        <textarea className="input-field" rows={2} maxLength={500} style={{ fontSize: 11, width: '100%' }} value={form.reason} placeholder="Ej.: el pago era de otra cotización" onChange={e => setForm(c => ({ ...c, reason: e.target.value }))} />
      </label>
      <label style={{ fontSize: 10, color: 'var(--globant-text)', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <input type="checkbox" checked={form.confirm} onChange={e => setForm(c => ({ ...c, confirm: e.target.checked }))} />
        Confirmo que lo registrado era un error y quiero corregirlo.
      </label>
      {err && <div role="alert" style={{ fontSize: 10, color: '#B4362C' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="action-btn btn-primary" style={{ fontSize: 10, flex: 1 }} disabled={saving || !form.confirm || !reasonOk || (form.status === 'Received' && !form.day)} onClick={save}>{saving ? 'Guardando…' : 'Guardar corrección'}</button>
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 10 }} disabled={saving} onClick={() => setForm(null)}>Cancelar</button>
      </div>
    </div>
  );
}
